import { useContext, useEffect } from 'react';
import type { VFC } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getLogger } from '@confluence/logger';

const logger = getLogger('notifications');

export const NotificationLandedEvent: VFC = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const isLivePage = useIsCurrentPageLive();

	// Check for the notification landed query params "atl_p" when loading
	useEffect(() => {
		const queryParams = getQueryParams();
		const base64NotificationParams = queryParams.atl_p as string;
		const aiInstrumentationParams = queryParams.atl_aip as string;

		if (fg('cc-notifications-fire-ai-analytics-from-email')) {
			if (aiInstrumentationParams) {
				try {
					const paramsJSONStr = window.atob(aiInstrumentationParams);
					const params = JSON.parse(paramsJSONStr);

					if (params && !params.id) {
						throw new Error('single instrumentation ID field is required to send event');
					} else if (params && !params.fn) {
						throw new Error('AI feature name field is required to send event');
					}

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'viewed',
							actionSubject: 'aiResult',
							actionSubjectId: 'client',
							attributes: {
								singleInstrumentationID: params.id,
								aiFeatureName: params.fn,
								proactiveGeneratedAI: 1,
								userGeneratedAI: 0,
								isAIFeature: 1,
								doesNotMeetMAUCriteria: false,
							},
							source: params.source || 'email',
						},
					}).fire();
				} catch (e) {
					logger.error`An error occurred attempting to send the AI instrumentation ID: ${e}`;

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'failed',
							actionSubject: 'aiResultViewedFromEmail',
							attributes: {
								error: e?.message || String(e),
								queryParams: aiInstrumentationParams,
							},
							source: 'email',
						},
					}).fire();
				}
			}
		}

		// If we have the query param for the event, send it and clear them
		if (base64NotificationParams) {
			// Its a Base64 encoded JSON string which we need to unpack
			try {
				const paramsJSONStr = window.atob(base64NotificationParams);
				const params = JSON.parse(paramsJSONStr);

				// Ensure we have a "source" field in the param
				if (params && !params.source) {
					throw new Error('source field is required to send event');
				}

				const attributes = Object.assign({ isLivePage }, params);
				delete attributes['source'];

				// COMMENTS-372 Persisting originId for cc-notifications to use
				// @ts-ignore
				window.atlp = { originId: params.i };

				// Send event
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'landed',
						actionSubject: 'notification',
						source: params.source,
						attributes,
					},
				}).fire();
			} catch (e) {
				logger.error`An error occurred attempting to decode the notifications query params: ${e}`;

				if (expValEquals('cc-notifications-track-notif-landed-failures', 'cohort', 'test')) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'failed',
							actionSubject: 'notificationLanded',
							attributes: {
								error: e?.message || String(e),
								queryParams: queryParams,
							},
							source: 'email',
						},
					}).fire();
				}
			}
		}

		// Unset the query params if either atl_p or atl_aip exists
		if (base64NotificationParams || aiInstrumentationParams) {
			setQueryParams({
				...queryParams,
				atl_aip: undefined,
				atl_p: undefined,
			});
		}
	}, [getQueryParams, createAnalyticsEvent, setQueryParams, isLivePage]);

	return null;
};
